import { isRouteErrorResponse } from '@remix-run/react'

const UNEXPECTED_ERROR = 'An unexpected error has occurred'
const ACCESS_DENIED_ERROR = 'Access denied'

interface ErrorResponse {
  errors: {
    [key: string]: string[]
  }
}

class FailedRequestError extends Error {
  response: Response

  constructor(message: string, response: Response) {
    super(message)
    this.response = response
  }

  static async create(response: Response): Promise<FailedRequestError> {
    const clonedForMessage = response.clone()
    const message =
      await FailedRequestError.extractErrorMessage(clonedForMessage)

    return new FailedRequestError(message, response)
  }

  private static async extractErrorMessage(
    response: Response,
  ): Promise<string> {
    try {
      const body = await response.json()

      if (body?.message) return body.message

      if (body?.errors && typeof body.errors === 'object')
        return concatenateErrors(body).join(', ')

      return UNEXPECTED_ERROR
    } catch {
      return UNEXPECTED_ERROR
    }
  }
}

function humanize(snakeCase: string): string {
  return snakeCase
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function concatenateErrors(errorBody: ErrorResponse): string[] {
  return Object.entries(errorBody.errors).reduce<string[]>(
    (acc, [field, messages]) => {
      const displayFieldName = humanize(field)

      const fieldErrors = messages.map(
        (message) => `${displayFieldName} ${message}`,
      )
      return acc.concat(fieldErrors)
    },
    [],
  )
}

function addSearchParams(
  url: URL,
  searchParams?: Record<string, string> | [string, string][],
): void {
  if (searchParams) {
    const iterable = Array.isArray(searchParams)
      ? searchParams
      : Object.entries(searchParams)
    for (const [key, value] of iterable) {
      url.searchParams.append(key, value)
    }
  }
}

function getAdminBaseUrl(): string {
  const { ADMIN_BASE_URL } = process.env
  if (!ADMIN_BASE_URL)
    throw new Error('ADMIN_BASE_URL env variable is required')

  return ADMIN_BASE_URL
}

async function handleFailedRequest(response: Response): Promise<never> {
  const contentType = response.headers.get('Content-Type')

  if (!contentType?.includes('application/json')) {
    throw new FailedRequestError(UNEXPECTED_ERROR, response)
  }
  const body = await response.clone().json()

  if (response.status === 401 && body?.message === ACCESS_DENIED_ERROR) {
    throw new Response(ACCESS_DENIED_ERROR, { status: 401 })
  }

  throw await FailedRequestError.create(response.clone())
}

function isAccessDeniedError(error: unknown): boolean {
  return (
    isRouteErrorResponse(error) &&
    error.status === 401 &&
    error.data === ACCESS_DENIED_ERROR
  )
}

export {
  FailedRequestError,
  addSearchParams,
  getAdminBaseUrl,
  handleFailedRequest,
  UNEXPECTED_ERROR,
  isAccessDeniedError,
  ACCESS_DENIED_ERROR,
}
